// notify 后台 SPA：登录页 + 记录列表/详情 + 飞书回调列表。
// - JWT 存 localStorage（access + refresh）。
// - fetch 包装：401 时用 refresh 续 access 并重试一次；refresh 也失效则回登录页。
// - 路由靠 hash（#login / #records / #callbacks），无第三方依赖。

const TOKEN_KEY = "notify_access";
const REFRESH_KEY = "notify_refresh";

// ---------- token ----------
function getAccess(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
function getRefresh(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
function setTokens(access: string, refresh: string) {
  localStorage.setItem(TOKEN_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}
function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// ---------- API ----------
async function api<T>(
  path: string,
  opts: RequestInit = {}
): Promise<T> {
  const headers = new Headers(opts.headers || {});
  headers.set("Accept", "application/json");
  if (opts.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const access = getAccess();
  if (access) headers.set("Authorization", `Bearer ${access}`);

  let resp = await fetch(path, { ...opts, headers });
  if (resp.status === 401) {
    // 尝试 refresh 一次。
    const refreshed = await tryRefresh();
    if (refreshed) {
      const newAccess = getAccess();
      if (newAccess) headers.set("Authorization", `Bearer ${newAccess}`);
      resp = await fetch(path, { ...opts, headers });
    }
  }
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try {
      const j = await resp.json();
      if (j.error) msg = j.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return resp.json() as Promise<T>;
}

async function tryRefresh(): Promise<boolean> {
  const refresh = getRefresh();
  if (!refresh) return false;
  try {
    const resp = await fetch("/api/v1/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!resp.ok) {
      clearTokens();
      return false;
    }
    const data = await resp.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    return true;
  } catch {
    clearTokens();
    return false;
  }
}

// ---------- 类型 ----------
interface RecordRow {
  id: string;
  business_id: string | null;
  event: string;
  level: string;
  channel: string;
  title: string;
  content: string;
  recipients: { target: string; kind: string }[];
  actions: { label: string; kind: string; value: string }[];
  event_time: string;
  metadata: Record<string, unknown>;
  status: string;
  attempts: number;
  last_error: string | null;
  created_at: string;
  sent_at: string | null;
}
interface ListResp<T> {
  page: number;
  page_size: number;
  items: T[];
}
interface HealthInfo {
  status: string;
  version: string;
  config_from: string;
  app: string;
}
interface CallbackRow {
  id: number;
  notification_id: string | null;
  action_value: string | null;
  operator: string | null;
  raw: unknown;
  received_at: string;
}

// ---------- 状态 ----------
let tab: "records" | "callbacks" = "records";
let healthCache: HealthInfo | null = null;

// ---------- 渲染入口 ----------
const app = document.getElementById("app")!;

function render() {
  if (!getAccess()) {
    renderLogin();
    return;
  }
  renderShell();
  if (tab === "records") void loadRecords();
  else void loadCallbacks();
}

// ---------- 登录页 ----------
function renderLogin() {
  app.innerHTML = `
    <div class="login-wrap">
      <form class="login-card" id="loginForm">
        <h1>Notify 后台</h1>
        <p>登录以管理通知投递记录</p>
        <label>用户名
          <input id="username" type="text" autocomplete="username" required />
        </label>
        <label>密码
          <input id="password" type="password" autocomplete="current-password" required />
        </label>
        <button class="btn" type="submit" id="loginBtn">登录</button>
        <div class="error" id="loginErr"></div>
      </form>
    </div>`;
  const form = document.getElementById("loginForm") as HTMLFormElement;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const err = document.getElementById("loginErr")!;
    err.textContent = "";
    const btn = document.getElementById("loginBtn") as HTMLButtonElement;
    btn.disabled = true;
    const username = (document.getElementById("username") as HTMLInputElement).value;
    const password = (document.getElementById("password") as HTMLInputElement).value;
    try {
      const resp = await fetch("/api/v1/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
        throw new Error(j.error || "登录失败");
      }
      const data = await resp.json();
      setTokens(data.access_token, data.refresh_token);
      render();
    } catch (e) {
      err.textContent = (e as Error).message;
    } finally {
      btn.disabled = false;
    }
  });
}

// ---------- 顶栏 + 标签 ----------
function renderShell() {
  app.innerHTML = `
    <div class="topbar">
      <span class="brand">Notify</span>
      <span class="badge" id="srcBadge">…</span>
      <span class="spacer"></span>
      <span class="user" id="meLabel"></span>
      <button class="btn btn-ghost" id="logoutBtn">退出</button>
    </div>
    <div class="tabs">
      <button id="tabRecords" class="${tab === "records" ? "active" : ""}">通知记录</button>
      <button id="tabCallbacks" class="${tab === "callbacks" ? "active" : ""}">飞书回调</button>
    </div>
    <div class="main" id="main"> <div class="loading">加载中…</div></div>`;

  document.getElementById("logoutBtn")!.addEventListener("click", () => {
    clearTokens();
    render();
  });
  document.getElementById("tabRecords")!.addEventListener("click", () => {
    tab = "records";
    document.getElementById("tabRecords")!.classList.add("active");
    document.getElementById("tabCallbacks")!.classList.remove("active");
  });
  document.getElementById("tabCallbacks")!.addEventListener("click", () => {
    tab = "callbacks";
    document.getElementById("tabCallbacks")!.classList.add("active");
    document.getElementById("tabRecords")!.classList.remove("active");
  });
  void loadHealth();
  void loadMe();
}

// ---------- health + me ----------
async function loadHealth() {
  try {
    healthCache = await api<HealthInfo>("/api/v1/health");
    const badge = document.getElementById("srcBadge")!;
    badge.textContent = `config: ${healthCache.config_from}`;
    badge.classList.toggle("nacos", healthCache.config_from === "nacos");
  } catch {
    /* 非致命 */
  }
}

async function loadMe() {
  try {
    const me = await api<{ username: string }>("/api/v1/auth/me");
    document.getElementById("meLabel")!.textContent = `👤 ${me.username}`;
  } catch {
    /* ignore */
  }
}

// ---------- 通知记录 ----------
let recPage = 1;
const recFilters = { channel: "", status: "", event: "", q: "" };

async function loadRecords() {
  const main = document.getElementById("main")!;
  main.innerHTML = `
    <div class="filters">
      <select id="fChannel">
        <option value="">全部渠道</option>
        <option value="feishu">飞书</option>
        <option value="email">邮件</option>
        <option value="sms">短信</option>
      </select>
      <select id="fStatus">
        <option value="">全部状态</option>
        <option value="pending">待发送</option>
        <option value="sent">已发送</option>
        <option value="failed">失败</option>
      </select>
      <input id="fEvent" placeholder="事件类型" />
      <button class="btn" id="searchBtn">查询</button>
      <span class="spacer" style="flex:1"></span>
      <span class="pager" id="recPageInfo"></span>
      <button class="btn btn-ghost" id="prevBtn">上一页</button>
      <button class="btn btn-ghost" id="nextBtn">下一页</button>
    </div>
    <div id="recTable"><div class="loading">加载中…</div></div>`;

  // 回填筛选
  (document.getElementById("fChannel") as HTMLSelectElement).value = recFilters.channel;
  (document.getElementById("fStatus") as HTMLSelectElement).value = recFilters.status;
  (document.getElementById("fEvent") as HTMLInputElement).value = recFilters.event;

  document.getElementById("searchBtn")!.addEventListener("click", () => {
    recFilters.channel = (document.getElementById("fChannel") as HTMLSelectElement).value;
    recFilters.status = (document.getElementById("fStatus") as HTMLSelectElement).value;
    recFilters.event = (document.getElementById("fEvent") as HTMLInputElement).value;
    recPage = 1;
    void fetchRecords();
  });
  document.getElementById("prevBtn")!.addEventListener("click", () => {
    if (recPage > 1) {
      recPage--;
      void fetchRecords();
    }
  });
  document.getElementById("nextBtn")!.addEventListener("click", () => {
    recPage++;
    void fetchRecords();
  });

  void fetchRecords();
}

async function fetchRecords() {
  const wrap = document.getElementById("recTable");
  if (!wrap) return;
  wrap.innerHTML = `<div class="loading">加载中…</div>`;
  const params = new URLSearchParams({ page: String(recPage), page_size: "20" });
  if (recFilters.channel) params.set("channel", recFilters.channel);
  if (recFilters.status) params.set("status", recFilters.status);
  if (recFilters.event) params.set("event", recFilters.event);
  try {
    const data = await api<ListResp<RecordRow>>(`/api/v1/records?${params}`);
    renderRecordTable(data);
  } catch (e) {
    wrap.innerHTML = `<div class="empty">加载失败：${(e as Error).message}</div>`;
  }
}

function renderRecordTable(data: ListResp<RecordRow>) {
  const wrap = document.getElementById("recTable")!;
  const info = document.getElementById("recPageInfo")!;
  info.textContent = `第 ${data.page} 页（每页 ${data.page_size}）`;
  if (!data.items.length) {
    wrap.innerHTML = `<div class="empty">暂无记录</div>`;
    return;
  }
  wrap.innerHTML = `
    <table>
      <thead><tr>
        <th>事件</th><th>标题</th><th>渠道</th><th>级别</th>
        <th>状态</th><th>尝试</th><th>发生时间</th>
      </tr></thead>
      <tbody>
        ${data.items
          .map(
            (r) => `
          <tr data-id="${r.id}">
            <td>${esc(r.event)}</td>
            <td>${esc(r.title)}</td>
            <td>${esc(r.channel)}</td>
            <td class="lvl-${esc(r.level)}">${esc(r.level)}</td>
            <td><span class="pill ${esc(r.status)}">${esc(r.status)}</span></td>
            <td>${r.attempts}</td>
            <td>${fmt(r.event_time)}</td>
          </tr>`
          )
          .join("")}
      </tbody>
    </table>`;
  wrap.querySelectorAll("tbody tr").forEach((tr) => {
    tr.addEventListener("click", () => {
      const id = (tr as HTMLElement).dataset.id!;
      void openRecord(id);
    });
  });
}

async function openRecord(id: string) {
  let rec: RecordRow;
  try {
    rec = await api<RecordRow>(`/api/v1/records/${id}`);
  } catch (e) {
    alert(`加载详情失败：${(e as Error).message}`);
    return;
  }
  const drawer = document.createElement("div");
  drawer.className = "drawer-backdrop";
  drawer.innerHTML = `
    <div class="drawer">
      <button class="btn btn-ghost close">✕ 关闭</button>
      <h2>${esc(rec.title)}</h2>
      <div class="kv">
        <span class="k">ID</span><span class="v">${esc(rec.id)}</span>
        <span class="k">事件</span><span class="v">${esc(rec.event)}</span>
        <span class="k">级别</span><span class="v lvl-${esc(rec.level)}">${esc(rec.level)}</span>
        <span class="k">渠道</span><span class="v">${esc(rec.channel)}</span>
        <span class="k">状态</span><span class="v"><span class="pill ${esc(rec.status)}">${esc(rec.status)}</span></span>
        <span class="k">尝试次数</span><span class="v">${rec.attempts}</span>
        <span class="k">业务ID</span><span class="v">${esc(rec.business_id || "-")}</span>
        <span class="k">发生时间</span><span class="v">${fmt(rec.event_time)}</span>
        <span class="k">入库时间</span><span class="v">${fmt(rec.created_at)}</span>
        <span class="k">发送时间</span><span class="v">${rec.sent_at ? fmt(rec.sent_at) : "-"}</span>
        ${rec.last_error ? `<span class="k">错误</span><span class="v" style="color:var(--red)">${esc(rec.last_error)}</span>` : ""}
      </div>
      <div class="section-title">正文</div>
      <pre>${esc(rec.content)}</pre>
      <div class="section-title">收件人</div>
      <pre>${esc(JSON.stringify(rec.recipients, null, 2))}</pre>
      ${
        rec.actions.length
          ? `<div class="section-title">动作</div><pre>${esc(JSON.stringify(rec.actions, null, 2))}</pre>`
          : ""
      }
      <div class="section-title">元数据</div>
      <pre>${esc(JSON.stringify(rec.metadata, null, 2) || "{}")}</pre>
    </div>`;
  drawer.addEventListener("click", (e) => {
    if (e.target === drawer || (e.target as HTMLElement).classList.contains("close")) {
      drawer.remove();
    }
  });
  document.body.appendChild(drawer);
}

// ---------- 飞书回调 ----------
let cbPage = 1;

async function loadCallbacks() {
  const main = document.getElementById("main")!;
  main.innerHTML = `
    <div class="filters">
      <input id="cbNid" placeholder="按 notification_id 过滤" />
      <button class="btn" id="cbSearch">查询</button>
      <span class="spacer" style="flex:1"></span>
      <span class="pager" id="cbPageInfo"></span>
      <button class="btn btn-ghost" id="cbPrev">上一页</button>
      <button class="btn btn-ghost" id="cbNext">下一页</button>
    </div>
    <div id="cbTable"><div class="loading">加载中…</div></div>`;
  document.getElementById("cbSearch")!.addEventListener("click", () => {
    cbPage = 1;
    void fetchCallbacks();
  });
  document.getElementById("cbPrev")!.addEventListener("click", () => {
    if (cbPage > 1) {
      cbPage--;
      void fetchCallbacks();
    }
  });
  document.getElementById("cbNext")!.addEventListener("click", () => {
    cbPage++;
    void fetchCallbacks();
  });
  void fetchCallbacks();
}

async function fetchCallbacks() {
  const wrap = document.getElementById("cbTable");
  if (!wrap) return;
  wrap.innerHTML = `<div class="loading">加载中…</div>`;
  const params = new URLSearchParams({ page: String(cbPage), page_size: "20" });
  const nid = (document.getElementById("cbNid") as HTMLInputElement | null)?.value?.trim();
  if (nid) params.set("notification_id", nid);
  try {
    const data = await api<ListResp<CallbackRow>>(`/api/v1/webhook/feishu/callbacks?${params}`);
    document.getElementById("cbPageInfo")!.textContent = `第 ${data.page} 页（每页 ${data.page_size}）`;
    if (!data.items.length) {
      wrap.innerHTML = `<div class="empty">暂无回调记录</div>`;
      return;
    }
    wrap.innerHTML = `
      <table>
        <thead><tr><th>ID</th><th>notification_id</th><th>操作人</th><th>动作值</th><th>时间</th></tr></thead>
        <tbody>
          ${data.items
            .map(
              (c) => `<tr>
                <td>${c.id}</td>
                <td>${esc(c.notification_id || "-")}</td>
                <td>${esc(c.operator || "-")}</td>
                <td>${esc(c.action_value || "-")}</td>
                <td>${fmt(c.received_at)}</td>
              </tr>`
            )
            .join("")}
        </tbody>
      </table>`;
  } catch (e) {
    wrap.innerHTML = `<div class="empty">加载失败：${(e as Error).message}</div>`;
  }
}

// ---------- 工具 ----------
function esc(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
function fmt(iso: string): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("zh-CN", { hour12: false });
  } catch {
    return iso;
  }
}

// ---------- 启动 ----------
render();
